<template>
  <div class="relative flex flex-row rounded-md border bg-white">
    <div
      v-if="selected"
      class="pointer-events-none absolute top-0 left-0 h-8 w-8 bg-green-600 pl-0.5 text-white [clip-path:polygon(0_0,100%_0,0_100%)]"
    >
      <FontAwesomeIcon :icon="['fas', 'check']" />
    </div>
    <div v-if="photo" class="flex w-1/5">
      <img
        v-if="item.master_image_url"
        class="aspect-square flex-grow-0 rounded-l-md object-cover"
        :alt="item.name"
        :src="item.master_image_url"
      />
      <div
        v-else
        class="flex flex-1 items-center justify-center rounded-l-md bg-gray-500 text-gray-700"
      ></div>
    </div>
    <div class="flex flex-col gap-1" :class="[photo ? 'w-4/5' : 'w-full']">
      <div class="p-2">
        <div class="flex flex-row items-center">
          <div class="flex flex-col">
            <h3
              class="capitalize-first flex-1 text-base leading-tight font-bold break-words text-gray-700"
            >
              {{ cName }}
            </h3>
            <h3
              v-if="cSubName"
              class="capitalize-first flex-1 text-xs leading-tight font-bold break-words text-gray-400"
            >
              #{{ cSubName }}
            </h3>
          </div>
          <span class="shrink-0">
            <FontAwesomeIcon
              v-if="item.favorite"
              :icon="['fas', 'star']"
              class="flex-shrink-0 text-yellow-300"
            />
          </span>
        </div>
        <div class="flex flex-row items-center justify-between gap-2">
          <span
            v-if="showCategory && item.category"
            class="bg-secondary text-contrast-secondary text-xxs rounded px-1"
          >
            {{ item.category.name[$i18n.locale] }}
          </span>
          <span v-if="item.ref" class="text-xs text-gray-500 select-none">
            # {{ item.ref }}
          </span>
        </div>
        <div class="mt-1 flex flex-row items-center justify-between gap-2">
          <div class="flex flex-row items-center gap-2">
            <ItemPricingSelector
              v-model:selected-pricing-id="selectedPricingId"
              :item="item"
              :hide-price="hidePrice"
              :currency="defaultCurrency"
            />
            <QuantityInput
              v-if="linkedComponents?.length > 0"
              v-show="!hideQuantityInput"
              v-model="quantity"
              :disabled="disabledQuantityInput"
            />
          </div>
          <button
            v-if="item.variants?.length > 0"
            class="bg-secondary text-contrast-secondary rounded border border-gray-300 px-2 py-0.5 text-center text-xs"
            @click="
              $emit('showVariants', [
                { ...item, variants: [] },
                ...item.variants
              ])
            "
          >
            <FontAwesomeIcon :icon="['fal', 'swatchbook']" />
          </button>
        </div>
        <div class="flex flex-col">
          <p v-if="item.remarks" v-html="displayedRemarks"></p>
        </div>
        <div
          v-if="linkedComponents?.length > 0"
          class="mt-1 flex flex-col gap-1 rounded bg-gray-50 px-2 py-1.5"
        >
          <span class="text-xxs font-semibold text-gray-500 uppercase">
            {{ $t('global.components_to_add') }}
          </span>
          <div
            v-for="component in linkedComponents"
            :key="component.id"
            class="flex items-center gap-1.5 rounded px-1 py-0.5 text-xs text-gray-600"
          >
            <button
              type="button"
              class="hover:bg-primary-50 flex min-w-0 flex-1 cursor-pointer items-center gap-1.5 rounded py-0.5 text-left select-none"
              @click="toggleComponent(component.id)"
            >
              <FontAwesomeIcon
                :icon="[
                  isComponentChecked(component.id) ? 'fas' : 'fal',
                  isComponentChecked(component.id) ? 'square-check' : 'square'
                ]"
                :class="
                  isComponentChecked(component.id)
                    ? 'text-primary'
                    : 'text-gray-400'
                "
                fixed-width
              />
              <span
                class="min-w-0 truncate"
                :title="component.name?.[$i18n.locale]"
                :class="{ 'text-gray-400 line-through': !isComponentChecked(component.id) }"
              >
                {{ component.name?.[$i18n.locale] }}
              </span>
            </button>
            <QuantityInput
              v-if="isComponentChecked(component.id) && !hideQuantityInput"
              class="shrink-0"
              :model-value="componentQuantities[component.id]"
              size="extra-small"
              :disabled="disabledQuantityInput"
              @update:model-value="
                (value) => updateComponentQuantity(component.id, value)
              "
            />
            <span
              v-else-if="isComponentChecked(component.id)"
              class="shrink-0"
            >
              {{ componentQuantities[component.id] }}x
            </span>
            <span
              v-if="!hidePrice"
              class="w-14 shrink-0 text-right font-medium"
              :class="{ 'text-gray-400 line-through': !isComponentChecked(component.id) }"
            >
              {{ componentDisplayPrice(component) }}
            </span>
          </div>
        </div>
        <div class="flex">
          <div v-if="item.stockable" class="flex shrink-0 flex-col">
            <div
              v-show="!hideStockDisponibility"
              class="flex shrink-0 flex-row gap-1.5 divide-x divide-slate-200 rounded bg-gray-100 px-1 py-0.5"
            >
              <div class="flex flex-row gap-1 px-1">
                <span><FontAwesomeIcon :icon="['far', 'warehouse']" /></span>
                <span class="font-bold">{{ item.stock?.count_available }}</span>
              </div>
              <div class="flex flex-row gap-1 px-1">
                <span>
                  <FontAwesomeIcon :icon="['far', 'boxes-stacked']" />
                </span>
                <span class="font-bold">{{ item.stock?.count_on_order }}</span>
              </div>
              <div class="flex flex-row gap-1 px-1">
                <span><FontAwesomeIcon :icon="['far', 'tags']" /></span>
                <span class="font-bold">{{ item.stock?.count_reserved }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="flex flex-col gap-1 bg-slate-50 p-2">
        <div
          v-if="linkedComponents?.length > 0 && !hidePrice"
          class="text-xxs text-right text-gray-500"
        >
          {{ $t('global.total_with_components') }}:
          <span class="font-semibold">
            {{ $h.formatCurrency(totalPriceWithComponents, defaultCurrency) }}
          </span>
        </div>
        <div class="flex flex-row items-center justify-end gap-2">
          <QuantityInput
            v-if="!(linkedComponents?.length > 0)"
            v-show="!hideQuantityInput"
            v-model="quantity"
            :disabled="disabledQuantityInput"
          />
          <BaseButton
            v-if="canAddItem"
            data-testid="add-item-button"
            :title="$t('button.add')"
            icon="plus"
            size="extra-small"
            :disabled="isSubmitting"
            @click="handleSelection(item)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import BaseButton from '@c/addf-package/components/BaseButton/BaseButton.vue'
import QuantityInput from '@ui/QuantityInput.vue'
import ItemPricingSelector from '@ui/modal/item/components/components/ItemPricingSelector.vue'

export default {
  name: 'ItemModalCard',
  components: {
    ItemPricingSelector,
    QuantityInput,
    BaseButton,
    FontAwesomeIcon
  },
  props: {
    item: {
      type: Object,
      required: true
    },
    linkedComponents: {
      type: Array,
      required: false,
      default: () => []
    },
    photo: {
      type: Boolean,
      required: false,
      default: false
    },
    selected: {
      type: Boolean,
      required: false,
      default: false
    },
    category: {
      type: String,
      required: false,
      default: null
    },
    hideQuantityInput: {
      type: Boolean,
      required: false,
      default: false
    },
    hidePrice: {
      type: Boolean,
      required: false,
      default: false
    },
    disabledQuantityInput: {
      type: Boolean,
      required: false,
      default: false
    },
    canAddItem: {
      type: Boolean,
      required: false,
      default: true
    },
    hideStockDisponibility: {
      type: Boolean,
      required: false,
      default: false
    },
    showCategory: {
      type: Boolean,
      required: false
    },
    currency: {
      type: String,
      required: false,
      default: null
    }
  },
  emits: ['selection', 'showVariants'],
  data() {
    return {
      quantity: this.item.quantity ?? 1,
      selectedPricingId: null,
      isSubmitting: false,
      componentQuantities: Object.fromEntries(
        this.linkedComponents.map((component) => [
          component.id,
          component.quantity ?? 1
        ])
      )
    }
  },
  computed: {
    ...mapGetters({
      invoice: 'invoicing/getInvoice',
      dossier: 'dossier/getDossier',
      currencySettings: 'metadata/getDefaultCurrencyMetadata',
      initialMetadata: 'metadata/getInitialMetadata'
    }),
    defaultCurrency() {
      return this.currency ?? this.currencySettings
    },
    displayedRemarks() {
      if (this.item.remarks[this.$i18n.locale])
        return this.item.remarks[this.$i18n.locale]
      if (typeof this.item.remarks === String) return this.item.remarks
      return null
    },
    cName() {
      const baseName = this.getName(this.item)
      const overrideName = this.getName(this.item?.item)

      return overrideName && overrideName !== baseName ? overrideName : baseName
    },
    cSubName() {
      const baseName = this.getName(this.item)
      const overrideName = this.getName(this.item?.item)

      return overrideName && overrideName !== baseName ? baseName : null
    },
    itemUnitPrice() {
      const priceEncodingType =
        this.initialMetadata?.settings?.price_encoding_type
      const price = this.item.advanced_pricing
        ? (this.item.prices?.find(
            (pricing) => pricing.id === this.selectedPricingId
          ) ?? this.item.prices?.[0])
        : this.item
      if (priceEncodingType === 'HTVA') {
        return parseFloat(
          price?.price_htva ?? this.item.default_price_htva ?? 0
        )
      }
      return parseFloat(
        price?.price_tvac ?? this.item.default_price_tvac ?? 0
      )
    },
    checkedComponents() {
      return this.linkedComponents.filter((component) =>
        this.isComponentChecked(component.id)
      )
    },
    totalPriceWithComponents() {
      const componentsTotal = this.checkedComponents.reduce(
        (total, component) =>
          total +
          this.componentUnitPrice(component) *
            (this.componentQuantities[component.id] ?? 1),
        0
      )
      return this.itemUnitPrice * this.quantity + componentsTotal
    }
  },
  mounted() {
    if (this.item?.advanced_pricing) {
      if (['invoice_info_route'].includes(this.$route.name)) {
        this.selectedPricingId =
          this.item.prices.filter((pricing) =>
            pricing.doc_roles.includes(this.invoice.doc_role)
          )?.[0]?.id ?? this.item.prices?.[0]?.id
      } else if (
        [
          'dossier_form_coffin_and_product_route_edit',
          'dossier_form_other_products_route_edit',
          'dossier_form_external_services_route_edit'
        ].includes(this.$route.name)
      ) {
        this.selectedPricingId =
          this.item.prices.filter((pricing) =>
            pricing.doc_roles.includes('ff')
          )?.[0]?.id ?? this.item.prices?.[0]?.id
      } else {
        this.selectedPricingId = this.item.prices?.[0]?.id
      }
    }
  },
  methods: {
    getName(obj) {
      if (!obj) return null
      return obj.name?.[this.$i18n.locale] ?? obj.name_index ?? null
    },
    componentUnitPrice(component) {
      const priceEncodingType =
        this.initialMetadata?.settings?.price_encoding_type
      const price = component.advanced_pricing
        ? component.prices?.[0]
        : component
      if (priceEncodingType === 'HTVA') {
        return parseFloat(
          price?.price_htva ?? component.default_price_htva ?? 0
        )
      }
      return parseFloat(price?.price_tvac ?? component.default_price_tvac ?? 0)
    },
    componentDisplayPrice(component) {
      return this.$h.formatCurrency(
        this.componentUnitPrice(component) *
          (this.componentQuantities[component.id] ?? 1),
        this.defaultCurrency
      )
    },
    isComponentChecked(componentId) {
      return componentId in this.componentQuantities
    },
    toggleComponent(componentId) {
      if (this.isComponentChecked(componentId)) {
        this.componentQuantities = Object.fromEntries(
          Object.entries(this.componentQuantities).filter(
            ([id]) => id !== componentId
          )
        )
        return
      }
      const component = this.linkedComponents.find((c) => c.id === componentId)
      this.componentQuantities = {
        ...this.componentQuantities,
        [componentId]: component?.quantity ?? 1
      }
    },
    updateComponentQuantity(componentId, value) {
      this.componentQuantities = {
        ...this.componentQuantities,
        [componentId]: value
      }
    },
    async handleSelection(item) {
      if (this.isSubmitting) return

      this.isSubmitting = true

      await this.$emit('selection', {
        quantity: this.quantity,
        item: item,
        selectPrice: this.selectedPricingId,
        selectedComponents: this.checkedComponents.map((component) => ({
          ...component,
          quantity: this.componentQuantities[component.id]
        }))
      })

      setTimeout(() => {
        this.isSubmitting = false
      }, 800)
    }
  }
}
</script>

<style>
input[type='number'].arrow-none::-webkit-inner-spin-button,
input[type='number'].arrow-none::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
</style>
